/**
 * storageQuota — localStorage quota management for the chat surface
 * (2026-08-22, Miles live incident: FIRESTORE INTERNAL ASSERTION b815 —
 * "QuotaExceededError: Failed to execute 'setItem' on 'Storage'" → full-page
 * crash boundary).
 *
 * Failure chain this module breaks
 * ──────────────────────────────────────────────────────────────────────
 * Per-thread transcript caches accumulate without bound (hundreds of
 * threads × workspaces). Once the origin's localStorage quota fills:
 *   • the Firestore web SDK's OWN setItem (watch-target metadata,
 *     'firestore_targets_*') throws → internal assertion b815 → the whole
 *     surface crashes to the error boundary (Miles's screenshot);
 *   • every app write silently fails (setToLocalStorage swallows), so
 *     reads return a FROZEN STALE state forever — stuck "Queued" behind a
 *     dead turn, wedge-detection reading a corpse as live, transcripts
 *     that "go missing" on reload.
 *
 * Strategy
 * ──────────────────────────────────────────────────────────────────────
 * The per-thread transcript caches (prest0_thread_msgs__*) are CACHES:
 * the server store is the durable copy (threadPersist additionally spills
 * to IndexedDB on quota). They are therefore the one key class that is
 * always safe to evict. This module:
 *   1. reclaimThreadCacheBytes(bytesNeeded, protect): evicts cold thread
 *      caches — largest-first among stale ones (newest-message timestamp
 *      older than RECENT_MS), then largest-first among the rest — never
 *      touching protected keys or any non-thread-cache key.
 *   2. quotaSafeSetItem(key, json): setItem with reclaim-and-retry on
 *      QuotaExceededError; latches a one-shot degraded warning when even
 *      eviction cannot make room. Wired as the chokepoint under
 *      utils.setToLocalStorage, so every app write self-heals.
 *   3. sweepStorageAtBoot(protect): proactive headroom — when estimated
 *      usage exceeds SOFT_LIMIT, evict down to TARGET. This is the only
 *      protection available to the Firestore SDK's own raw setItem calls,
 *      which cannot be wrapped.
 *
 * No imports from utils/threadPersist (they sit above this module — the
 * chokepoint wiring would cycle). The key prefix is duplicated from
 * threadPersist.THREAD_MSG_KEY_PREFIX and pinned equal by test. The one
 * import is threadCacheTier, which imports nothing: a thread write this
 * module finally cannot land is HELD there, so the rest of this tab never
 * reads the frozen copy as current (2026-09-23, the newest-message flicker).
 */

import { holdRefusedWrite, releaseHeldCopy } from './threadCacheTier';

/** Lockstep duplicate of threadPersist.THREAD_MSG_KEY_PREFIX (test-pinned). */
export const THREAD_CACHE_KEY_PREFIX = 'prest0_thread_msgs__';

/** Usage above this at boot triggers the proactive sweep (bytes, UTF-16). */
export const SOFT_LIMIT_BYTES = 3_500_000;
/** The proactive sweep evicts down to roughly this usage. */
export const TARGET_BYTES = 2_500_000;
/** Caches whose newest message is younger than this are evicted last. */
export const RECENT_MS = 48 * 60 * 60 * 1000;
/** Emergency reclaim frees at least this much beyond the failed write. */
const MIN_RECLAIM_BYTES = 262_144;

let degradedWarned = false;

function storageOrNull(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function isQuotaExceededError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; code?: number };
  return (
    e.name === 'QuotaExceededError'
    || e.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || e.code === 22
    || e.code === 1014
  );
}

/** Rough origin usage in bytes (UTF-16: 2 bytes per code unit, key + value). */
export function estimateLocalStorageBytes(): number {
  const ls = storageOrNull();
  if (!ls) return 0;
  let total = 0;
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (key === null) continue;
      const value = ls.getItem(key);
      total += (key.length + (value ? value.length : 0)) * 2;
    }
  } catch {
    /* mid-iteration storage failure — return what was counted */
  }
  return total;
}

type CacheCandidate = {
  key: string;
  bytes: number;
  /** newest message timestamp (ms) — 0 when unparseable (evict first). */
  newestTs: number;
};

/**
 * Newest-message timestamp of one cached transcript, parsing only the tail
 * entries. Unparseable/corrupt values return 0 so they rank as oldest.
 */
function newestTimestampOf(json: string | null): number {
  if (!json) return 0;
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr) || arr.length === 0) return 0;
    let newest = 0;
    for (let i = arr.length - 1; i >= 0 && i >= arr.length - 3; i -= 1) {
      const t = arr[i]?.timestamp;
      const ms = typeof t === 'string' || typeof t === 'number' ? Date.parse(String(t)) || Number(t) || 0 : 0;
      if (ms > newest) newest = ms;
    }
    return newest;
  } catch {
    return 0;
  }
}

function collectCandidates(ls: Storage, protect: Set<string>): CacheCandidate[] {
  const out: CacheCandidate[] = [];
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (key === null) continue;
      if (!key.startsWith(THREAD_CACHE_KEY_PREFIX)) continue;
      if (protect.has(key)) continue;
      const value = ls.getItem(key);
      out.push({ key, bytes: (key.length + (value ? value.length : 0)) * 2, newestTs: -1 });
    }
  } catch {
    /* partial enumeration is fine */
  }
  return out;
}

/**
 * Evict cold per-thread caches until `bytesNeeded` is freed (or candidates
 * run out). Round 1 evicts caches whose newest message is older than
 * RECENT_MS, largest first (max bytes for min parses); round 2, only if
 * still short, evicts the remaining unprotected caches, oldest first.
 * Returns the bytes freed. Never touches non-thread-cache keys.
 */
export function reclaimThreadCacheBytes(bytesNeeded: number, protect?: Iterable<string>): number {
  const ls = storageOrNull();
  if (!ls) return 0;
  const protectSet = new Set(protect ?? []);
  const candidates = collectCandidates(ls, protectSet);
  if (candidates.length === 0) return 0;
  candidates.sort((a, b) => b.bytes - a.bytes);

  let freed = 0;
  const evicted: string[] = [];
  const recent: CacheCandidate[] = [];
  for (const c of candidates) {
    if (freed >= bytesNeeded) break;
    c.newestTs = newestTimestampOf(ls.getItem(c.key));
    if (c.newestTs > 0 && Date.now() - c.newestTs < RECENT_MS) {
      recent.push(c);
      continue;
    }
    try { ls.removeItem(c.key); } catch { continue; }
    freed += c.bytes;
    evicted.push(c.key);
  }
  if (freed < bytesNeeded) {
    recent.sort((a, b) => a.newestTs - b.newestTs);
    for (const c of recent) {
      if (freed >= bytesNeeded) break;
      try { ls.removeItem(c.key); } catch { continue; }
      freed += c.bytes;
      evicted.push(c.key);
    }
  }
  if (evicted.length > 0) {
    console.warn('[storageQuota] evicted cold thread caches (server store is the durable copy)', {
      count: evicted.length,
      freedBytes: freed,
    });
  }
  return freed;
}

/**
 * setItem with reclaim-and-retry on quota exhaustion. Returns true when the
 * value is stored. A false return means the bin is full even after evicting
 * every cold cache — logged once (latched) so the console shows the degraded
 * state without a write-storm.
 */
export function quotaSafeSetItem(key: string, json: string, protect?: Iterable<string>): boolean {
  const ls = storageOrNull();
  if (!ls) return false;
  try {
    ls.setItem(key, json);
    degradedWarned = false;
    releaseHeldCopy(key);
    return true;
  } catch (err) {
    if (!isQuotaExceededError(err)) return false;
  }
  const needed = Math.max((key.length + json.length) * 2 * 4, MIN_RECLAIM_BYTES);
  reclaimThreadCacheBytes(needed, new Set([key, ...(protect ?? [])]));
  try {
    ls.setItem(key, json);
    degradedWarned = false;
    releaseHeldCopy(key);
    return true;
  } catch (retryErr) {
    // The bin is full even after eviction. For a thread cache the disk copy is
    // now FROZEN behind what this tab holds — keep this tab's copy where every
    // read of the key will find it (threadCacheTier), instead of letting the
    // frozen one read as current.
    if (key.startsWith(THREAD_CACHE_KEY_PREFIX)) holdRefusedWrite(key, json);
    if (!degradedWarned) {
      degradedWarned = true;
      console.error('[storageQuota] localStorage still full after eviction — writes are degraded', {
        key,
        valueBytes: json.length * 2,
        error: retryErr instanceof Error ? retryErr.message : String(retryErr),
      });
    }
    return false;
  }
}

/**
 * Proactive boot-time sweep: when usage exceeds SOFT_LIMIT, evict cold
 * thread caches down to TARGET. This is the only protection for the
 * Firestore SDK's own raw localStorage writes (the b815 crash class) —
 * emergency reclaim can't intercept those, headroom prevents them.
 * Returns the bytes freed (0 when under the limit).
 */
export function sweepStorageAtBoot(protect?: Iterable<string>): number {
  const used = estimateLocalStorageBytes();
  if (used <= SOFT_LIMIT_BYTES) return 0;
  const freed = reclaimThreadCacheBytes(used - TARGET_BYTES, protect);
  console.warn('[storageQuota] boot sweep ran', { usedBytes: used, freedBytes: freed });
  return freed;
}
